// Vista mobile-first para vendedora · solo chat WhatsApp, sin sidebar admin.
// Diseñado para PWA instalable en celular.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { theme } from '../styles/theme';

type Convo = {
  id: string;
  phone_e164: string;
  contact_name: string | null;
  customer_id: string | null;
  lead_score: number;
  lead_label: string;
  intent_summary: string | null;
  needs_human: boolean;
  escalation_reason: string | null;
  last_inbound_at: string | null;
  message_count: number;
  takeover_by_user_id: string | null;
  customer?: { business_name: string } | null;
};

type Message = {
  id: string;
  direction: 'inbound' | 'outbound';
  sender: string;
  body: string | null;
  status: string;
  created_at: string;
};

const LABEL_COLORS: Record<string, string> = {
  caliente: '#DC2626',
  tibio: '#F59E0B',
  frio: '#3B82F6',
  nuevo: '#6B7280',
  cliente_existente: '#10B981',
};

export function Vendedora() {
  const { session, signOut } = useAuth();
  const nav = useNavigate();
  const [convos, setConvos] = useState<Convo[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [composeText, setComposeText] = useState('');
  const [sending, setSending] = useState(false);
  const [view, setView] = useState<'list' | 'chat'>('list');

  useEffect(() => {
    if (!session) { nav('/login?redirect=/vendedora'); return; }
    loadConvos();
  }, [session]);

  useEffect(() => {
    if (selectedId) {
      loadMessages(selectedId);
      setView('chat');
    }
  }, [selectedId]);

  useEffect(() => {
    const ch = supabase.channel('vendedora_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'whatsapp_messages' }, () => {
        if (selectedId) loadMessages(selectedId);
        loadConvos();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'whatsapp_conversations' }, loadConvos)
      .subscribe();
    return () => { ch.unsubscribe(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  async function loadConvos() {
    const { data } = await supabase.from('whatsapp_conversations')
      .select('id, phone_e164, contact_name, customer_id, lead_score, lead_label, intent_summary, needs_human, escalation_reason, last_inbound_at, message_count, takeover_by_user_id, customer:customers(business_name)')
      .eq('status', 'active')
      .order('lead_score', { ascending: false })
      .limit(40);
    setConvos((data as any) ?? []);
  }

  async function loadMessages(id: string) {
    const { data } = await supabase.from('whatsapp_messages')
      .select('id, direction, sender, body, status, created_at')
      .eq('conversation_id', id)
      .order('created_at', { ascending: true });
    setMessages((data as any) ?? []);
  }

  async function send() {
    if (!composeText.trim() || !selectedId) return;
    setSending(true);
    const { data: { session: s } } = await supabase.auth.getSession();
    await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp-send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${s?.access_token ?? ''}` },
      body: JSON.stringify({ conversation_id: selectedId, text: composeText.trim() }),
    });
    setComposeText('');
    setSending(false);
    if (selectedId) loadMessages(selectedId);
  }

  // Pedir permiso de notificaciones al cargar
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  if (!session) return null;

  const sel = convos.find(c => c.id === selectedId);
  const hotCount = convos.filter(c => c.lead_score >= 70).length;
  const humanCount = convos.filter(c => c.needs_human).length;

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.bg,
      color: theme.text,
      fontFamily: theme.font.sans,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Top bar */}
      <div style={{
        background: theme.brand,
        color: theme.textOnBrand,
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        {view === 'chat' && sel ? (
          <>
            <button
              onClick={() => { setView('list'); setSelectedId(null); }}
              style={{ background: 'transparent', border: 'none', color: theme.textOnBrand, fontSize: 24, cursor: 'pointer' }}
            >←</button>
            <div style={{ flex: 1, marginLeft: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>
                {sel.customer?.business_name || sel.contact_name || sel.phone_e164}
              </div>
              <div style={{ fontSize: 10, opacity: 0.8 }}>
                {sel.phone_e164}
              </div>
            </div>
            <ScoreBadge score={sel.lead_score} label={sel.lead_label} />
          </>
        ) : (
          <>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 0.5 }}>NULED</div>
              <div style={{ fontSize: 11, opacity: 0.8 }}>Vendedora</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {hotCount > 0 && (
                <span style={{ background: '#DC2626', color: 'white', padding: '4px 8px', fontSize: 11, fontWeight: 700, borderRadius: 12 }}>
                  🔥 {hotCount}
                </span>
              )}
              {humanCount > 0 && (
                <span style={{ background: theme.accent, color: theme.brand, padding: '4px 8px', fontSize: 11, fontWeight: 700, borderRadius: 12 }}>
                  👤 {humanCount}
                </span>
              )}
              <button
                onClick={async () => { await signOut(); nav('/login'); }}
                style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: theme.textOnBrand, padding: '4px 10px', fontSize: 10, cursor: 'pointer' }}
              >Salir</button>
            </div>
          </>
        )}
      </div>

      {/* Main content */}
      {view === 'list' ? (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {convos.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted }}>
              Sin conversaciones activas.<br />
              Cuando un cliente escriba aparecerá aquí.
            </div>
          ) : convos.map(c => (
            <button
              key={c.id}
              onClick={() => setSelectedId(c.id)}
              style={{
                width: '100%',
                background: theme.bg,
                border: 'none',
                borderBottom: `1px solid ${theme.border}`,
                padding: '14px 16px',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: theme.font.sans,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: theme.brand, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.customer?.business_name || c.contact_name || c.phone_e164}
                    {c.needs_human && <span style={{ marginLeft: 6, color: '#DC2626' }}>👤</span>}
                  </div>
                  <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.intent_summary || 'Sin contexto'}
                  </div>
                  <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 4 }}>
                    {c.message_count} msj · {c.last_inbound_at ? new Date(c.last_inbound_at).toLocaleString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '—'}
                  </div>
                </div>
                <ScoreBadge score={c.lead_score} label={c.lead_label} />
              </div>
            </button>
          ))}
        </div>
      ) : (
        <>
          {/* Chat timeline */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 6, background: '#F9FAFB' }}>
            {sel?.intent_summary && (
              <div style={{ fontSize: 11, color: theme.brand, padding: 8, background: theme.accent + '30', borderRadius: 6, marginBottom: 8 }}>
                <strong>AI:</strong> {sel.intent_summary}
              </div>
            )}
            {messages.map(m => {
              const isInbound = m.direction === 'inbound';
              const isAI = m.sender === 'agent_ai';
              return (
                <div
                  key={m.id}
                  style={{
                    alignSelf: isInbound ? 'flex-start' : 'flex-end',
                    maxWidth: '80%',
                    background: isInbound ? 'white' : isAI ? '#DCFCE7' : theme.accent,
                    padding: '8px 12px',
                    borderRadius: 12,
                    fontSize: 14,
                    lineHeight: 1.4,
                    color: theme.text,
                    border: `1px solid ${theme.border}`,
                  }}
                >
                  <div style={{ fontSize: 9, color: theme.textMuted, marginBottom: 4 }}>
                    {isInbound ? 'Cliente' : isAI ? '🤖 AI' : '👤 Tú'} · {new Date(m.created_at).toLocaleString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{m.body || '—'}</div>
                </div>
              );
            })}
          </div>

          {/* Composer */}
          <div style={{ padding: 10, borderTop: `1px solid ${theme.border}`, background: theme.bg, display: 'flex', gap: 8 }}>
            <textarea
              value={composeText}
              onChange={e => setComposeText(e.target.value)}
              placeholder="Mensaje..."
              rows={1}
              style={{
                flex: 1, padding: '10px 12px', border: `1px solid ${theme.border}`,
                fontSize: 14, fontFamily: theme.font.sans, resize: 'none', outline: 'none',
                color: theme.text, background: theme.bg, borderRadius: 18,
              }}
            />
            <button
              onClick={send}
              disabled={sending || !composeText.trim()}
              style={{
                background: theme.accent, color: theme.brand, border: 'none',
                padding: '10px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                fontFamily: theme.font.sans, borderRadius: 18,
                opacity: sending || !composeText.trim() ? 0.5 : 1,
              }}
            >
              ✈
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function ScoreBadge({ score, label }: { score: number; label: string }) {
  const color = LABEL_COLORS[label] || '#6B7280';
  return (
    <div style={{
      background: color, color: 'white',
      padding: '4px 8px', fontSize: 10, fontWeight: 700,
      textTransform: 'uppercase', borderRadius: 3,
    }}>
      {score}
    </div>
  );
}
